<template lang="pug">
    v-flex(layout mx-auto row wrap)
        v-flex(xs12 mb-3 layout row align-center)
            v-icon mdi-account-search
            v-divider.ml-3
        v-flex(xs12)
            v-text-field(v-model="userSearch" clearable ref="refSearch"
            :loading="userSearchTyping || userSearching"
                type="search"
                @input="userSearchTyping = true"
                solo
                :hide-details="true"
                persistent-hint
                prepend-inner-icon="mdi-magnify"
                :label="$t('general.findUser')")
                template(#append-outer)
                    v-menu
                        template(#activator='{ on: menu }')
                            v-tooltip(bottom)
                                template(#activator='{ on: tooltip }')
                                    v-btn(v-on='{ ...tooltip, ...menu }' :disabled="advanceSearchDisabled"
                                        icon style="top: -5px" @click="showAdvanceSearchDialog")
                                        v-icon mdi-tune
                                span {{$t('pages.settings.calendar.searchConfigHint')}}
                    v-menu(v-if="usersSearched.length")
                        template(#activator='{ on: menu }')
                            v-tooltip(bottom)
                                template(#activator='{ on: tooltip }')
                                    v-btn(v-on='{ ...tooltip, ...menu }'
                                        icon style="top: -5px" @click="onClearSearch")
                                        v-icon mdi-eraser
                                span {{$t('general.clearSearchUsersSearched')}}
        v-flex(xs12 v-if="usersSearched.length === 0 && userSearch" mt-2)
            v-alert(type="error" dense elevation="1" icon="mdi-emoticon-sad-outline") {{$t('general.searchNotFound')}}
        div(v-else-if="usersSearched.length" style="width: 100%")
            .body-2.mt-2 {{$t('general.searchResult')}} {{usersSearched.length}}
            v-card(class="xs12 mt-2" tag="v-flex" style="max-height: 200px; overflow: auto" )
                v-list(dense two-line)
                    v-list-item(v-for='item in usersSearchedForDisplay' :key='item.id' @click="" style="cursor: default;")
                        v-list-item-avatar
                            v-img(:src='item.avatar')
                        v-list-item-content
                            v-list-item-title
                                highlighter(:text="item.name" :keyword="userSearchedKeyword")
                            v-list-item-subtitle
                                span.primary--text {{item.company.code}}
                                span &nbsp;&mdash;&nbsp;
                                highlighter(:text="item.email" :keyword="userSearchedKeyword")
                        v-list-item-action
                            v-icon(color="deep-purple accent-4" @click="addUser(item)" v-if="!usersSelected.some(u => u.id === item.id)") mdi-plus-circle-outline
                            v-icon(color="grey" v-else) mdi-cancel

        v-flex(xs12 mb-3 mt-3 layout row align-center)
            v-icon mdi-account-group
            v-divider.ml-3

        v-flex(xs12)
            v-data-table(:headers="tableHeaders" :items="usersSelected" :mobile-breakpoint="50" :disable-sort="true").elevation-2
                template(#item.action='{ item }')
                    v-layout(pa-0 ma-0 justify-center)
                        v-btn(icon @click='removeUser(item)')
                            v-icon mdi-delete-outline

        advanced-search-user-dialog(ref="refAdvancedSearchUserDialog" @submit="onSearchAdvanceSubmit"
            :search-companies-label="$t('pages.settings.user.searchCompanies')"
            :search-types-label="$t('pages.settings.user.table.headers.type.title')"
            :title="$t('pages.settings.user.searchConfigHint')")
</template>

<script>
import qs from "qs";
import cloneDeep from "lodash/cloneDeep";
import debounce from "lodash/debounce";
import get from "lodash/get";
import auth from "../../mixins/auth";
import general from "../../mixins/general";
import AdvancedSearchUserDialog from "./AdvancedSearchUserDialog";

export default {
  name: "UserPermissionFinder",
  components: { AdvancedSearchUserDialog },
  mixins: [auth, general],
  props: {
    advanceSearchDisabled: {
      type: Boolean,
      default: false
    },
    usersSelected: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      userSearch: null,
      userSearchedKeyword: null,
      userSearchTyping: false,
      userSearching: false,
      usersSearched: [],
      cancelSearchUser: null,
      searchSelectedCompanies: [],
      searchSelectedTypes: []
    };
  },
  computed: {
    tableHeaders: {
      get() {
        return [
          {
            text: this.$t(
              "pages.settings.user.table.headers['company.code'].title"
            ),
            align: "left",
            sortable: true,
            value: "company.code",
            class: ["body-2"]
          },
          {
            text: this.$t("pages.settings.user.table.headers.name.title"),
            value: "name",
            sortable: true,
            class: ["body-2", "tdx"]
          },
          {
            text: this.$t("pages.settings.user.table.headers.email.title"),
            value: "email",
            sortable: true,
            class: ["body-2"]
          },
          {
            text: this.$t("general.remove"),
            value: "action",
            align: "center",
            class: ["body-2"],
            sortable: false
          }
        ];
      }
    },
    usersSearchedForDisplay() {
      return this.usersSearched.map(user => ({
        ...user,
        avatar: user.avatar || this.userAvatarColor
      }));
    }
  },
  watch: {
    userSearch: debounce(function() {
      this.userSearchTyping = false;
    }, 500),
    userSearchTyping(value, before) {
      if (before && !value) {
        this.doSearchUser();
      }
    }
  },
  methods: {
    onClearSearch() {
      this.usersSearched = [];
      this.userSearch = null;
    },
    onSearchAdvanceSubmit({ companies, types }) {
      this.searchSelectedCompanies = companies;
      this.searchSelectedTypes = types;
      this.doSearchUser();
    },
    showAdvanceSearchDialog() {
      this.$refs["refAdvancedSearchUserDialog"].show();
    },
    addUser(user) {
      if (!this.usersSelected.some(u => u.id === user.id)) {
        this.$emit("update:users-selected", [
          ...this.usersSelected,
          cloneDeep(user)
        ]);
      }
    },
    removeUser(user) {
      const index = this.usersSelected.findIndex(u => u.id === user.id);
      if (index > -1) {
        this.usersSelected.splice(index, 1);
      }
    },
    cancelSearchRequest() {
      if (this.userSearching && typeof this.cancelSearchUser === "function") {
        this.cancelSearchUser("Searching cancelled by user");
        this.userSearching = false;
      }
    },
    doSearchUser() {
      if (
        !this.userSearch &&
        this.searchSelectedCompanies.length === 0 &&
        this.searchSelectedTypes.length === 0
      ) {
        this.usersSearched = [];
        return;
      }
      this.cancelSearchRequest();
      let queryString = qs.stringify({
        perPage: -1,
        keyword: this.userSearch,
        searchBy: ["users.name", "users.email"],
        filters: {
          status: [true],
          companies: this.searchSelectedCompanies,
          types: this.searchSelectedTypes
        }
      });
      let self = this;
      this.userSearching = true;
      this.axios
        .get(`users?${queryString}`, {
          cancelToken: new this.axios.CancelToken(function executor(c) {
            self.cancelSearchUser = c;
          })
        })
        .then(async response => {
          // const usersSearched = response.data.data.map(user => ({
          //     ...user,
          //     avatar: user.avatar || this.userAvatarColor
          // }));

          const usersSearched = response.data.data;

          this.userSearchedKeyword = get(
            response.data,
            "additions.keyword",
            null
          );

          if ("result" in this.$listeners) {
            await this.$emit("result", { finder: this, users: usersSearched });
          } else {
            this.usersSearched = usersSearched;
          }
        })
        .finally(() => {
          this.userSearching = false;
        });
    },
    close() {
      this.cancelSearchRequest();
      this.$refs["refAdvancedSearchUserDialog"].selectedCompanies = [];
      this.$refs["refAdvancedSearchUserDialog"].selectedTypes = [];
      this.searchSelectedCompanies = [];
      this.searchSelectedTypes = [];
    },
    clear() {
      this.usersSearched = [];
      this.userSearchedKeyword = null;
      this.$emit("update:users-selected", []);
      this.userSearch = null;
    },
    focusSearch() {
      this.$refs["refSearch"].focus();
    }
  }
};
</script>

<style scoped>
.table >>> td,
th {
  /*width: 50%;*/
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
